
import React, { useState } from "react";
import {
    Phone,
    Send,
    MessageSquare,
    User,
    AtSign
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { createInquiry } from "@/services/inquiry";
import { Loader2 } from "lucide-react";
import { normalizePhoneNumber, isValidPhoneNumber } from "@/utils/phone";
import { countryCodes } from "@/constants";

interface ContactFormProps {
    onSuccess?: () => void;
    className?: string;
    showHeading?: boolean;
}

const ContactForm: React.FC<ContactFormProps> = ({ onSuccess, className, showHeading = true }) => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
    });
    const [countryCode, setCountryCode] = useState<string>(countryCodes[0].value);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Phone validation & normalization
        const fullPhone = countryCode + formData.phone;
        const normalizedPhone = normalizePhoneNumber(fullPhone);
        if (!isValidPhoneNumber(normalizedPhone)) {
            toast({
                variant: "destructive",
                title: "Invalid Phone Number",
                description: "Please enter a valid phone number with country code (e.g., +971501234567)",
            });
            return;
        }


        setIsSubmitting(true);

        try {
            await createInquiry({
                name: formData.name,
                email: formData.email,
                phone: normalizedPhone,
                message: formData.message,
                inquiryType: "general",
            });

            toast({
                title: "Message Sent",
                description: "We've received your message and will get back to you soon!",
            });

            setFormData({
                name: "",
                email: "",
                phone: "",
                message: "",
            });

            if (onSuccess) {
                onSuccess();
            }
        } catch (error: any) {
            console.error("Error sending message:", error);
            toast({
                variant: "destructive",
                title: "Error",
                description: error.message || "Failed to send message. Please try again.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={`bg-card p-6 md:p-6 rounded-xl shadow-sm ${className || ''}`}>
            {showHeading && (
                <div className="flex items-center gap-2 mb-6">
                    <MessageSquare className="h-5 w-5 text-primary" />
                    <h2 className="text-2xl font-semibold">Send Message</h2>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-4">
                {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4"> */}
                <div className="space-y-2">
                    <div className="relative">
                        <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Your Name"
                            className="pl-10"
                            required
                        />
                    </div>
                </div>
                <div className="space-y-2">
                    <div className="relative">
                        <AtSign className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Your Email"
                            className="pl-10"
                            required
                        />
                    </div>
                </div>
                {/* </div> */}

                <div className="space-y-2">
                    <div className="flex gap-2">
                        <Select value={countryCode} onValueChange={setCountryCode}>
                            <SelectTrigger className="w-[110px]">
                                <SelectValue placeholder="Code" />
                            </SelectTrigger>
                            <SelectContent>
                                {countryCodes.map((code) => (
                                    <SelectItem key={code.label} value={code.value}>
                                        {code.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <div className="relative flex-1">
                            <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                            <Input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="Contact No"
                                className="pl-10"
                                required
                            />
                        </div>
                    </div>
                </div>



                <div className="space-y-2">
                    <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Your Message"
                        rows={4}
                        required
                    />
                </div>

                <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                    {isSubmitting ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                        <Send className="mr-2 h-4 w-4" />
                    )}
                    {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
            </form>
        </div>
    );
};

export default ContactForm;
